import { useEffect, useMemo, useState } from "react";
import { Clock3, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { CLINIC_TIME_ZONE } from "@/lib/clinicTime";

export default function AppointmentOpsTimeline({ appointmentId }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!appointmentId) {
      setPayload(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    supabase.rpc("list_appointment_ops_timeline", {
      p_appointment_id: appointmentId,
    }).then(({ data, error: rpcError }) => {
      if (cancelled) return;
      if (rpcError) {
        setError(rpcError.message || "Could not load timeline.");
        setPayload(null);
        return;
      }
      setPayload(data || {});
    }).catch((err) => {
      if (!cancelled) setError(err?.message || "Could not load timeline.");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [appointmentId]);

  const events = useMemo(() => {
    return [...(payload?.events || [])].sort((a, b) => {
      return new Date(a.occurred_at || 0) - new Date(b.occurred_at || 0);
    });
  }, [payload?.events]);

  return (
    <section className="border-t border-slate-100 pt-3" data-testid="appointment-ops-timeline">
      <div className="flex items-center gap-2">
        <Clock3 className="h-4 w-4 text-slate-500" />
        <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">Activity timeline</p>
      </div>
      {loading ? (
        <div className="mt-2 flex items-center gap-2 rounded bg-slate-50 px-3 py-3 text-xs text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading timeline...
        </div>
      ) : error ? (
        <div role="alert" className="mt-2 rounded bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
      ) : events.length === 0 ? (
        <p className="mt-2 rounded bg-slate-50 px-3 py-3 text-xs font-medium text-slate-500">No timeline events yet.</p>
      ) : (
        <ol className="mt-2 space-y-2">
          {events.map((event) => (
            <TimelineEvent key={event.event_id || `${event.event_type}:${event.occurred_at}`} event={event} />
          ))}
        </ol>
      )}
    </section>
  );
}

function TimelineEvent({ event }) {
  return (
    <li className="grid grid-cols-[80px_1fr] gap-3 rounded bg-slate-50 px-3 py-2">
      <time className="text-[11px] font-semibold text-slate-500" dateTime={event.occurred_at || undefined}>
        {formatTime(event.occurred_at)}
      </time>
      <div className="min-w-0">
        <p className="text-xs font-bold text-slate-900">{event.title || humanize(event.event_type)}</p>
        <p className="text-[11px] text-slate-500">{event.actor_name || "System"}</p>
        {event.detail ? <p className="mt-0.5 text-xs text-slate-600">{event.detail}</p> : null}
      </div>
    </li>
  );
}

function formatTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: CLINIC_TIME_ZONE,
  });
}

function humanize(value) {
  return String(value || "Event")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
